import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Toast } from "primereact/toast";

import { addCustomer } from "../../../services/clearing/customers";

import { Customer } from "../../../types/customer";
import CustomerForm from "../components/forms/CustomerForm";
import { validateCustomerForm } from "../utils/customerValidation";

const AddCustomer = () => {
  // move empty customer initialisation inside component to have it reset properly
  const emptyCustomer: Customer = {
    account_holder_name: "",
    iban: "",
    station_id: "",
    bic: "",
    email: "",
    recipient_emails: [],
    phone: "",
    detailed_invoice: false,
    attach_csv_to_credit_note: false,
    address: {
      company_name: "",
      recipient_name: "",
      street_address: "",
      country: "DE",
      postal_code: "",
      city: "",
    },
    invoice_language: "DE",
    vat_id: null,
    business_type: "PRIVATE",
  };

  const [customer, setCustomer] = useState(emptyCustomer);

  const toast = useRef(null);
  const navigate = useNavigate();

  const handleChange = (field: string, value: any, nested = false) => {
    if (nested) {
      // supports nested property accessors, e.g. address.street or address.recipient.name
      setCustomer((prev) => {
        const newCustomer = { ...prev };
        const keys = field.split(".");
        let current = newCustomer;

        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== "object") {
            current[key] = {}; // create object if missing
          }
          current = current[key];
        }

        current[keys[keys.length - 1]] = value;

        return newCustomer;
      });
    } else {
      setCustomer((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async () => {
    const missingFields = validateCustomerForm(customer);

    if (missingFields.length > 0) {
      toast?.current.show({
        severity: "error",
        summary: "Missing Required Fields",
        detail: `Please fill in the following fields: ${missingFields.join(
          ", "
        )}`,
        life: 5000,
      });
      return;
    }

    try {
      await addCustomer(customer);
      toast?.current.show({
        severity: "success",
        summary: "Success",
        detail: "Customer added",
        life: 3000,
      });

      // Navigate back to customer list
      setTimeout(() => {
        navigate("/customers");
      }, 1500);

      // reset form fields
      setCustomer(emptyCustomer);
    } catch {
      toast?.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to add customer",
        life: 3000,
      });
    }
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <CustomerForm
        submitLabel="Save"
        title="Customer Details"
        customer={customer}
        onFieldChange={handleChange}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default AddCustomer;
